// Pure, local text measurements. No model, domain intent, or network calls.
export const words = (text) => text.match(/[\p{L}\p{N}]+(?:[._/'-][\p{L}\p{N}]+)*/gu) || [];

export function textDelta(before, after) {
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start++;
  let endA = before.length, endB = after.length;
  while (endA > start && endB > start && before[endA - 1] === after[endB - 1]) { endA--; endB--; }
  return { start, inserted: after.slice(start, endB), deleted: before.slice(start, endA) };
}

export function specificity(tokens, config, stopwords = []) {
  const vague = new Set(config.vague_words), stop = new Set(stopwords);
  let concrete = 0, ambiguous = 0, anchors = 0;
  tokens.forEach((token) => {
    const t = token.toLowerCase();
    if (vague.has(t)) ambiguous++;
    else if (!stop.has(t)) concrete++;
    if (/\d|[._/]/.test(t)) anchors++;
  });
  const evidence = concrete + anchors * config.anchor_bonus;
  const denominator = evidence + ambiguous * config.vague_weight;
  return { score: denominator ? evidence / denominator : 1, concrete, ambiguous, anchors };
}

export function buildEvent(prevText, text, cursor, lastCalledText, config, stopwords, baseline) {
  const delta = textDelta(prevText, text);
  const since = textDelta(lastCalledText, text);
  const tokens = words(text.slice(0, cursor ?? text.length));
  const current = specificity(tokens.slice(-config.window_words), config, stopwords);
  const preceding = tokens.slice(-2 * config.window_words, -config.window_words);
  const reference = baseline ?? (preceding.length ? specificity(preceding, config, stopwords).score : null);
  return {
    word_count: words(text).length,
    words_since_last_call: Math.max(words(since.inserted).length, words(since.deleted).length),
    insert_chars: delta.inserted.length, delete_chars: delta.deleted.length,
    inserted_words: words(delta.inserted).length,
    specificity: current.score, vague_tokens: current.ambiguous,
    baseline: reference,
    relative_drop: reference > 0 ? Math.max(0, (reference - current.score) / reference) : 0,
    explicit_detail: new RegExp(config.explicit_detail_pattern, "i").test(since.inserted),
    unchanged: text === lastCalledText,
  };
}

export function decide(config, event, hasCalled = false) {
  const decision = (rule_id, action = "skip") => ({ ...event, rule_id, action });
  if (!event.word_count) return decision("empty");
  if (config.skip_if_unchanged && event.unchanged) return decision("unchanged");
  if (event.inserted_words >= config.large_insert_words || event.insert_chars >= config.large_insert_chars)
    return decision("large_insert", "call_now");
  if (event.word_count < config.min_words) return decision("word_floor");
  if (hasCalled && event.words_since_last_call < config.min_words_between_calls) return decision("word_spacing");
  if (event.specificity < config.specificity_threshold) return decision("low_specificity", "call");
  if (event.relative_drop >= config.relative_drop_threshold) return decision("specificity_drop", "call");
  if (event.explicit_detail) return decision("explicit_detail", "call");
  return decision("specific_enough");
}